import { ListItemProps } from "@/components/ListItem/ListItem";
import Tooltip from "@/components/Tooltip";
import { TagType } from "@/components/Tag/Tag";
import { MetricCardVariant } from "@/components/MetricCard/MetricCard.types";
import {
  formattedAddressLines,
  formatAddressCopyText,
  getAddressIconName,
  getAddressTypeLabel
} from "@/lib/addressHelpers";
import { isInFuture, toFormat, toUTCFormat } from "@/lib/dateHelpers";
import { Path } from "@/router";
import {
  Address,
  CcmsSuspension,
  Employment,
  Gender,
  Person,
  PhoneNumber,
  SectionAffiliation,
  ProgramStatus as ProgramStatusType,
  ProgramTypeName,
  PlayingAbilityTestStatus
} from "@/types";
import { capitalize, get, includes, sortBy } from "lodash";
import { DateTime } from "luxon";
import { getPATSuspensionDetails } from "./pat";
import { getConsolidatedPATStatusLabel } from "./pat-status";
import getAge from "@/lib/ageHelper";
import Icon from "@/components/Icon";
import Button from "@/components/Button";

// Person utility functions
export const hasDuesPayingProgram = ({ latestProgram }: Person) =>
  [ProgramTypeName.Member, ProgramTypeName.Associate, ProgramTypeName.Affiliate].includes(
    latestProgram?.programType?.name!
  ) && latestProgram?.status !== ProgramStatusType.TERMINATED;

export function isMemberOrAssociateProgram(person: Person): boolean {
  const programName = person.latestProgram?.programType?.name;
  return programName === ProgramTypeName.Member || programName === ProgramTypeName.Associate;
}

/**
 * Checks if a person is an active Member or Associate
 */
export const isNotTerminatedMemberOrAssociate = (person: Person): boolean => {
  const programTypeName = get(person, "latestProgram.programType.name");
  const status = get(person, "latestProgram.status");
  const memberOrAssociateTypes = [ProgramTypeName.Member, ProgramTypeName.Associate];
  const notTerminatedStatusTypes = [ProgramStatusType.ACTIVE, ProgramStatusType.SUSPENDED];

  return includes(memberOrAssociateTypes, programTypeName) && includes(notTerminatedStatusTypes, status);
};

/**
 * Checks if an employment has actually ended (endedOn exists and is in the past)
 */
export const hasEmploymentEnded = (employment: { endedOn?: string | null }): boolean => {
  if (!employment.endedOn) return false;
  const endedDate = DateTime.fromISO(employment.endedOn);
  return endedDate.isValid && endedDate <= DateTime.now();
};

/**
 * Primary employment that is still current: null endedOn, unparseable endedOn, or a future endedOn.
 * Matches Career Overview `getPrimaryOrCurrentEmployment`.
 */
export const getCurrentPrimaryEmployment = <T extends { isPrimary?: boolean | null; endedOn?: string | null }>(
  employments?: T[] | null
): T | undefined => employments?.find((employment) => employment.isPrimary && !hasEmploymentEnded(employment));

const HIDE_UNEMPLOYED_FOR_CLASSIFICATIONS = new Set(["HM", "LM", "LMA", "LMC", "LMM", "LMMA", "MP", "RM", "RSV"]);

export const shouldSuppressUnemployedForClassification = (person: Person): boolean => {
  const bandClassificationName = person.latestProgram?.band?.classification?.name;
  if (!bandClassificationName) return false;
  return HIDE_UNEMPLOYED_FOR_CLASSIFICATIONS.has(bandClassificationName);
};

export function buildTelHref(phone: PhoneNumber): string | null {
  const sanitizedPhoneNumber = phone.phoneNumber?.replace(/[^\d+]/g, "") ?? "";
  if (!sanitizedPhoneNumber) return null;

  const sanitizedExtension = phone.phoneExtension?.replace(/\D/g, "");
  return sanitizedExtension ? `tel:${sanitizedPhoneNumber};ext=${sanitizedExtension}` : `tel:${sanitizedPhoneNumber}`;
}

export function buildSmsHref(phone: PhoneNumber): string | null {
  const sanitizedPhoneNumber = phone.phoneNumber?.replace(/[^\d+]/g, "") ?? "";
  if (!sanitizedPhoneNumber) return null;

  return `sms:${sanitizedPhoneNumber}`;
}

export function buildMailtoHref(email?: string | null): string | null {
  const trimmedEmail = email?.trim();
  if (!trimmedEmail) return null;

  return `mailto:${trimmedEmail}`;
}

export function buildEmailHeadline(email?: string | null) {
  const href = buildMailtoHref(email);
  if (!href) return email;

  return (
    <a
      href={href}
      onClick={(event) => event.stopPropagation()}
      className="text-sys-brand-on-surface hover:text-sys-brand-primary cursor-pointer text-body-large-semibold"
    >
      {email?.trim()}
    </a>
  );
}

export function getMobileAndHomePhones(phoneNumbers?: PhoneNumber[]) {
  return {
    mobile: phoneNumbers?.find((phone) => phone.phoneType === "mobile"),
    home: phoneNumbers?.find((phone) => phone.phoneType === "home")
  };
}

export function phoneOptions(phoneNumbers?: PhoneNumber[]): ListItemProps[] {
  const { mobile: mobilePhone, home: homePhone } = getMobileAndHomePhones(phoneNumbers);

  const options: ListItemProps[] = [];

  const buildPhoneHeadline = (phone: PhoneNumber) => {
    const href = buildTelHref(phone);
    if (!href) return phone.phoneFormatted;

    return (
      <a
        href={href}
        onClick={(event) => event.stopPropagation()}
        className="text-sys-brand-on-surface hover:text-sys-brand-primary cursor-pointer text-body-large-semibold"
      >
        {phone.phoneFormatted}
      </a>
    );
  };

  // Mobile phone (always shown)
  if (mobilePhone) {
    options.push({
      iconName: "call",
      headline: buildPhoneHeadline(mobilePhone),
      overline: capitalize(mobilePhone.phoneType),
      copyable: !!mobilePhone.phoneFormatted,
      copyText: mobilePhone.phoneFormatted ?? undefined
    });
  } else {
    options.push({
      iconName: "call",
      overline: "Mobile",
      headline: <span className="opacity-20">--</span>
    });
  }

  // Home phone (always shown)
  if (homePhone) {
    options.push({
      iconName: "call",
      headline: buildPhoneHeadline(homePhone),
      overline: capitalize(homePhone.phoneType),
      copyable: !!homePhone.phoneFormatted,
      copyText: homePhone.phoneFormatted ?? undefined
    });
  } else {
    options.push({
      iconName: "call",
      overline: "Home",
      headline: <span className="opacity-20">--</span>
    });
  }

  return options;
}

export function emailOptions(email?: string): ListItemProps[] {
  const trimmedEmail = email?.trim();

  return [
    {
      iconName: "mail",
      headline: buildEmailHeadline(trimmedEmail),
      copyable: !!trimmedEmail,
      copyText: trimmedEmail
    }
  ];
}

function addressHeadline(addressLines: ReturnType<typeof formattedAddressLines>, prefix?: string) {
  return (
    <>
      {prefix && (
        <>
          <span>{prefix}</span>
          <br />
        </>
      )}
      <span>{addressLines.line1}</span>
      <br />
      <span>{addressLines.line2}</span>
      {addressLines.line3 && (
        <>
          <br />
          <span>{addressLines.line3}</span>
        </>
      )}
    </>
  );
}

export type AddressWithFacilityName = Address & { facilityName?: string };

const CONTACT_PREFERENCE_ADDRESS_SORT_ORDER = ["home", "secondary", "main", "mailing"];

function sortContactPreferenceAddresses(addresses: AddressWithFacilityName[]) {
  return sortBy(addresses, (address) => {
    const index = CONTACT_PREFERENCE_ADDRESS_SORT_ORDER.indexOf(address.addressType);
    return index === -1 ? CONTACT_PREFERENCE_ADDRESS_SORT_ORDER.length : index;
  });
}

export function visibleContactAddressIds(addresses: Address[] = [], employments: Employment[] = []): Set<string> {
  const ids = new Set<string>();

  for (const address of addresses) {
    if (address.id) ids.add(address.id);
  }

  for (const employment of employments.filter((employment) => !employment.endedOn)) {
    for (const address of employment.facility?.addresses ?? []) {
      if (address.id) ids.add(address.id);
    }
  }

  return ids;
}

export function facilityNameForAddress(
  addressId: string,
  employments: Employment[] = []
): { facilityName?: string; ended: boolean } {
  for (const employment of employments) {
    const facilityAddress = employment.facility?.addresses?.find((address) => address.id === addressId);
    if (facilityAddress) {
      return { facilityName: employment.facility.name, ended: !!employment.endedOn };
    }
  }

  return { ended: false };
}

export function buildContactAddressListItem(
  contactAddress: Address,
  contactAddressId?: string,
  employments: Employment[] = []
): ListItemProps {
  const { facilityName, ended } = facilityNameForAddress(contactAddress.id, employments);
  const addressLines = formattedAddressLines(contactAddress);
  const copyText = formatAddressCopyText(addressLines, facilityName);
  const overlineLabel = facilityName
    ? `Facility ${getAddressTypeLabel(contactAddress.addressType)}`
    : getAddressTypeLabel(contactAddress.addressType);

  return {
    iconName: getAddressIconName(contactAddress.addressType),
    iconBackground: true,
    overline: overlineLabel,
    copyable: true,
    copyText,
    headline: addressHeadline(addressLines, facilityName),
    supportingText: ended && facilityName ? "Former facility" : undefined,
    ...contactPreferenceText(contactAddress, contactAddressId)
  };
}

function orphanedContactPreferenceAddress(person: Person): AddressWithFacilityName | null {
  const { contactAddress, contactAddressId, addresses = [], employments = [] } = person;

  if (!contactAddress?.id || !contactAddressId || contactAddress.id !== contactAddressId) {
    return null;
  }

  if (visibleContactAddressIds(addresses, employments).has(contactAddress.id)) {
    return null;
  }

  const { facilityName } = facilityNameForAddress(contactAddress.id, employments);
  return { ...contactAddress, facilityName };
}

export function orphanedContactPreferenceOption(person: Person): ListItemProps | null {
  const orphanedAddress = orphanedContactPreferenceAddress(person);

  if (!orphanedAddress) {
    return null;
  }

  return buildContactAddressListItem(orphanedAddress, person.contactAddressId, person.employments ?? []);
}

export function buildContactPreferenceAddressOptions(person: Person): AddressWithFacilityName[] {
  const activeEmploymentAddresses: AddressWithFacilityName[] = (person.employments ?? [])
    .filter((employment) => !employment.endedOn)
    .flatMap((employment) =>
      employment.facility.addresses.map((address) => ({
        ...address,
        facilityName: employment.facility.name
      }))
    );

  const choices: AddressWithFacilityName[] = [...person.addresses, ...activeEmploymentAddresses];
  const orphanedAddress = orphanedContactPreferenceAddress(person);

  if (orphanedAddress) {
    choices.push(orphanedAddress);
  }

  return sortContactPreferenceAddresses(choices);
}

export function addressOptions(
  addresses: Address[] = [],
  contactAddressId?: string,
  contactAddress?: Address,
  showAllAddressesWhenNoPreference: boolean = true,
  employments?: Employment[]
): ListItemProps[] {
  const homeAddress = addresses.find((address) => address.addressType === "home");
  const secondaryAddress = addresses.find((address) => address.addressType === "secondary");

  const options: ListItemProps[] = [];

  // If there is a preferred contact address, show it
  if (contactAddress) {
    options.push(buildContactAddressListItem(contactAddress, contactAddressId, employments ?? []));
  } else {
    // No preferred address specified
    // Home address (always shown)
    if (homeAddress) {
      const addressLines = formattedAddressLines(homeAddress);
      options.push({
        iconName: getAddressIconName("home"),
        overline: getAddressTypeLabel("home"),
        copyable: true,
        copyText: formatAddressCopyText(addressLines),
        headline: addressHeadline(addressLines),
        ...contactPreferenceText(homeAddress, contactAddressId)
      });
    } else {
      options.push({
        iconName: getAddressIconName("home"),
        overline: getAddressTypeLabel("home"),
        headline: <span className="opacity-20">--</span>
      });
    }

    // Secondary/Alternate Mailing Address (only shown when showAllAddressesWhenNoPreference is true)
    if (showAllAddressesWhenNoPreference) {
      if (secondaryAddress) {
        const addressLines = formattedAddressLines(secondaryAddress);
        options.push({
          iconName: getAddressIconName("secondary"),
          overline: getAddressTypeLabel("secondary"),
          copyable: true,
          copyText: formatAddressCopyText(addressLines),
          headline: addressHeadline(addressLines),
          ...contactPreferenceText(secondaryAddress, contactAddressId)
        });
      } else {
        options.push({
          iconName: getAddressIconName("secondary"),
          overline: getAddressTypeLabel("secondary"),
          headline: <span className="opacity-20">--</span>
        });
      }
    }
  }

  return options;
}

export interface FacilitySection {
  heading: string;
  options: ListItemProps[];
  headingHelpIcon?: boolean;
}

export function facilitySections(employments: Employment[] | undefined, contactAddressId?: string): FacilitySection[] {
  const activeEmployments = employments ?? [];
  const sections = activeEmployments
    .filter((e) => !e.endedOn)
    .map((employment) => {
      const { facility } = employment;
      const addresses = facility.addresses || [];
      const mainAddress = addresses.find((a) => a.addressType === "main") || addresses[0];
      const mailingAddress = addresses.find((a) => a.addressType === "mailing");

      const options: ListItemProps[] = [];

      // Main address entry
      if (mainAddress) {
        const addressLines = formattedAddressLines(mainAddress);
        options.push({
          iconName: "location_on",
          iconBackground: true,
          overline: getAddressTypeLabel("main"),
          copyable: true,
          copyText: formatAddressCopyText(addressLines),
          headline: addressHeadline(addressLines),
          ...contactPreferenceText(mainAddress, contactAddressId),
          action: facility.id ? () => window.open(`/facilities/${facility.id}/details/contact`, "_blank") : undefined,
          actionIcon: "navigate_next"
        });
      }

      // Alternate Mailing Address entry
      if (mailingAddress) {
        const addressLines = formattedAddressLines(mailingAddress);
        options.push({
          iconName: "markunread_mailbox",
          iconBackground: true,
          overline: getAddressTypeLabel("mailing"),
          copyable: true,
          copyText: formatAddressCopyText(addressLines),
          headline: addressHeadline(addressLines),
          ...contactPreferenceText(mailingAddress, contactAddressId),
          action: facility.id ? () => window.open(`/facilities/${facility.id}/details/contact`, "_blank") : undefined,
          actionIcon: "navigate_next"
        });
      }

      return {
        heading: `Facility - ${facility.name}`,
        options,
        headingHelpIcon: true
      } as FacilitySection;
    });

  // Filter out sections with no options and sort by primary facility first
  return sections
    .filter((section) => section.options.length > 0)
    .sort((a, b) => {
      const aIsPrimary = activeEmployments.find(
        (e) => !e.endedOn && e.facility.name === a.heading.replace("Facility - ", "")
      )?.isPrimary;
      const bIsPrimary = activeEmployments.find(
        (e) => !e.endedOn && e.facility.name === b.heading.replace("Facility - ", "")
      )?.isPrimary;
      if (aIsPrimary) return -1;
      if (bIsPrimary) return 1;
      return 0;
    });
}

export function contactPreferenceText(address?: Address, contactAddressId?: string): Partial<ListItemProps> {
  if (address && address.id === contactAddressId) {
    return { tagText: "Contact Preference", tagType: "information" as TagType };
  } else {
    return {};
  }
}

export const genderOptions = Object.entries(Gender).map(([value, key]) => ({
  value: key,
  label: value
}));

const getGenderLabel = (gender: Gender) => genderOptions.find((g) => g.value === gender)?.label;

const RACE_LABELS: Record<string, string> = {
  A: "Asian/Pacific Islander",
  B: "African American",
  C: "Caucasian",
  H: "Hispanic/Latino",
  I: "American Indian/Aleut/Eskimo",
  M: "Multi-racial/Ethnic",
  O: "Other"
};

export const getRaceLabel = (race?: string | null) => {
  if (!race) return undefined;
  return RACE_LABELS[race.toUpperCase()] ?? race;
};

export function getPersonAge(person: Person): number | null {
  const isDeceased = person.latestProgram?.band?.terminatedReason?.toLowerCase() === "deceased";

  if (person.age !== undefined && person.age !== null && !isDeceased) {
    return person.age;
  }

  if (!person.dob?.year || !person.dob?.month || !person.dob?.day) {
    return null;
  }

  const dateOfBirth = `${person.dob.year}-${String(person.dob.month).padStart(2, "0")}-${String(person.dob.day).padStart(2, "0")}`;
  const deathDate = isDeceased ? person.latestProgram?.band?.terminatesOn : undefined;

  return getAge(dateOfBirth, deathDate ?? undefined);
}

export function isPersonUnder18(person: Person): boolean {
  const age = getPersonAge(person);
  return age !== null && age < 18;
}

export function personalOptions(person: Person): ListItemProps[] {
  let details: ListItemProps[] = [];

  details.push({
    iconName: "cake",
    overline: "Date of Birth",
    headline: dobHeadline(person)
  });

  details.push({
    iconName: "person",
    overline: "Age",
    headline: ageHeadline(person)
  });

  if (isPersonUnder18(person)) {
    details.push({
      iconName: "verified_user",
      overline: "Event Registration Parental Consent",
      headline:
        person.eventRegistrationParentalConsent == null
          ? undefined
          : person.eventRegistrationParentalConsent
            ? "On file"
            : "Not on file"
    });
  }

  const genderLabel = getGenderLabel(person.gender!);

  details.push({
    iconName: "person",
    overline: "Gender Identity",
    headline: genderLabel
  });

  details.push({
    iconName: "person",
    overline: "Ethnicity",
    headline: getRaceLabel(person.race)
  });

  details.push({
    iconName: "badge",
    overline: "Social Security Number",
    headline: ssnHeadline(person)
  });

  details.push({
    iconName: "person",
    overline: "Preferred First Name",
    headline: person.informalName
  });

  details.push({
    iconName: "person",
    overline: "Preferred Last Name",
    headline: person.preferredLastName
  });

  details.push({
    iconName: "badge",
    overline: "Member Card Fulfillment",
    headline:
      person.memberCardFulfillment == null ? undefined : person.memberCardFulfillment ? "Requested" : "Not Requested"
  });

  details.push({
    iconName: "person",
    overline: "Spouse Full Name",
    headline: person.spouseName
  });

  details.push({
    iconName: "badge",
    overline: "Spouse Card Fulfillment",
    headline:
      person.spouseCardFulfillment == null ? undefined : person.spouseCardFulfillment ? "Requested" : "Not Requested"
  });

  details.push({
    iconName: "badge",
    overline: "PGA Directory Visibility",
    headline: person.directoryOptOut == null ? undefined : person.directoryOptOut ? "Opted Out" : "Visible"
  });

  return details;
}

const fullName = ({ firstName, middleName, lastName }: Person) => [firstName, middleName, lastName].join(" ");

const ssnHeadline = (person: Person) => {
  if (!person.ssn) return null;

  const ssnNumber = person.ssn.full || person.ssn.last4;

  return ssnNumber ? (
    <Tooltip
      variant="plain"
      color="black"
      supportingText={maskedSSN(ssnNumber)}
      menuCorner="end-start"
      anchorCorner="start-start"
    >
      {maskedSSN()}
    </Tooltip>
  ) : (
    maskedSSN()
  );
};

const maskedSSN = (initialSSN?: string) => {
  // Remove any existing dashes first
  let ssn = initialSSN ? initialSSN.replace(/-/g, "") : "";
  ssn = (ssn || "").padStart(9, "•");
  return `${ssn.slice(0, 3)}-${ssn.slice(3, 5)}-${ssn.slice(5)}`;
};

const ageHeadline = (person: Person) => {
  if (!person.dob) return null;

  // Check if person is deceased and calculate age using death date
  const isDeceased = person.latestProgram?.band?.terminatedReason?.toLowerCase() === "deceased";

  // Use provided age from backend if present and member is not deceased
  if (person.age !== undefined && !isDeceased) {
    return String(person.age);
  }

  if (isDeceased) {
    // Calculate age manually, considering death date for deceased members
    const { day, month, year } = person.dob;
    if (!day || !month || !year) return "••";

    const dateOfBirth = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const deathDate = person.latestProgram?.band?.terminatesOn;
    const calculatedAge = getAge(dateOfBirth, deathDate);

    return calculatedAge ? String(calculatedAge) : "••";
  }

  // For non-deceased members, maintain original masking behavior
  return "••";
};

const dobHeadline = (person: Person) => {
  if (!person.dob) return null;

  const formattedDate = formatDob(person);
  const masked = "••/••/••••";

  return formattedDate ? (
    <Tooltip
      variant="plain"
      color="black"
      supportingText={formattedDate}
      menuCorner="end-start"
      anchorCorner="start-start"
    >
      {masked}
    </Tooltip>
  ) : (
    masked
  );
};

function formatDob(person: Person) {
  const { day, month, year } = person.dob || {};
  if (!day) return null;

  const date = DateTime.fromObject({ day, month, year });
  if (year) {
    return date.toFormat("MMM d, yyyy");
  } else {
    return date.toFormat("MMM d");
  }
}

export function officialDetailsOptions(person: Person, canEditLastName: boolean = false): ListItemProps[] {
  const { name: classificationName, description: classificationDesc } =
    person.latestProgram?.band?.classification || {};
  const status = person.latestProgram?.status;
  const hasNameChangeDocument = !!person.nameChangeDocument;
  const nameChangeDocumentUrl = person.nameChangeDocument?.url;

  const documentIconTooltip =
    hasNameChangeDocument && nameChangeDocumentUrl && canEditLastName ? (
      <div className="flex items-center self-center">
        <Tooltip
          variant="rich"
          positioning="fixed"
          anchorCorner="start-start"
          menuCorner="end-start"
          closeDelay={500}
          title="Last Name Change Documentation"
          supportingText="View proof of legal last name change"
          actions={{
            primary: (
              <Button
                variant="outlined"
                icon="open_in_new"
                onClick={() => {
                  if (nameChangeDocumentUrl) {
                    window.open(nameChangeDocumentUrl, "_blank", "noopener,noreferrer");
                  }
                }}
              >
                View Document
              </Button>
            )
          }}
        >
          <Icon name="description" size={24} className="text-sys-brand-on-surface-variant" />
        </Tooltip>
      </div>
    ) : null;

  return [
    {
      iconName: "person",
      overline: "Legal Full Name",
      headline: fullName(person),
      ...(documentIconTooltip && { renderActions: () => documentIconTooltip })
    },
    {
      iconName: "person",
      overline: "Name Suffix",
      headline: person.suffixName
    },
    {
      iconName: "badge",
      overline: "PGA ID",
      headline: person.pgaId!
    },
    {
      iconName: "badge",
      overline: "Classification",
      headline: classificationName,
      supportingText: classificationDesc
    },
    {
      iconName: "badge",
      overline: "Status",
      headline: capitalize(status)
    }
  ];
}

export function sectionOptions(
  person: Person,
  showChapterAffiliation: boolean = false,
  navigate?: (to: Path) => void
): ListItemProps[] {
  const activeAffiliations = person.activeSectionAffiliations || [];
  const primaryAffiliation =
    person.primarySectionAffiliation ||
    activeAffiliations.find((affiliation) => affiliation.primary) ||
    activeAffiliations[0];
  const secondaryAffiliations = activeAffiliations.filter((affiliation) => affiliation.id !== primaryAffiliation?.id);

  const date = primaryAffiliation?.startAt;
  const formattedDate = date ? toFormat(date) : null;
  const duesPayingProgram = hasDuesPayingProgram(person);
  const fallbackChapterName = (affiliation?: SectionAffiliation) => affiliation?.chapterAffiliationDescription?.trim();
  const chapterNamesForAffiliation = (affiliation?: SectionAffiliation) => {
    const chapterNames = affiliation?.chapterAffiliations
      ?.map((chapterAffiliation) => chapterAffiliation.chapter?.name?.trim())
      .filter((chapterName): chapterName is string => !!chapterName);
    if (chapterNames && chapterNames.length > 0) return chapterNames;

    const fallback = fallbackChapterName(affiliation);
    return fallback ? [fallback] : [];
  };

  const sectionAction =
    person.pgaId && navigate ? () => navigate(`/people/${person.pgaId}/membership/section` as Path) : undefined;

  const options: ListItemProps[] = [
    {
      iconName: "domain",
      overline: duesPayingProgram ? "Primary Section" : "Located In",
      headline: primaryAffiliation?.section?.name || person.primarySection?.name,
      ...(formattedDate && {
        tooltipTitle: "Since",
        tooltipSupportingText: formattedDate
      }),
      action: sectionAction
    }
  ];

  if (showChapterAffiliation) {
    const chapterNames = chapterNamesForAffiliation(primaryAffiliation);
    if (chapterNames.length === 0) {
      options.push({
        iconName: "villa",
        overline: "Section Chapter",
        headline: <span className="opacity-20">--</span>
      });
    } else {
      chapterNames.forEach((chapterName) => {
        options.push({
          iconName: "villa",
          overline: "Section Chapter",
          headline: chapterName
        });
      });
    }
  }

  secondaryAffiliations.forEach((affiliation) => {
    const secondaryStartDate = affiliation.startAt ? toFormat(affiliation.startAt) : null;

    options.push({
      className: "pt-2",
      iconName: "domain",
      overline: "Secondary Section",
      headline: affiliation.section?.name,
      ...(secondaryStartDate && {
        tooltipTitle: "Since",
        tooltipSupportingText: secondaryStartDate
      }),
      action: sectionAction
    });

    if (showChapterAffiliation) {
      const chapterNames = chapterNamesForAffiliation(affiliation);
      if (chapterNames.length === 0) {
        options.push({
          iconName: "villa",
          overline: "Section Chapter",
          headline: <span className="opacity-20">--</span>
        });
      } else {
        chapterNames.forEach((chapterName) => {
          options.push({
            iconName: "villa",
            overline: "Section Chapter",
            headline: chapterName
          });
        });
      }
    }
  });

  return options;
}

export function committeesOptions(person: Person, navigate?: (to: Path) => void): ListItemProps[] {
  if (![ProgramTypeName.Member, ProgramTypeName.Associate].includes(person.latestProgram?.programType.name!)) {
    return [];
  }

  const activeCommitteeNames =
    person.committeeHistory
      ?.filter((committee) => !committee.endDate || isInFuture(committee.endDate))
      .map((committee) => committee.committeeName) ?? [];

  return [
    {
      iconName: "groups",
      overline: "Active Committees",
      headline: activeCommitteeNames.length ? activeCommitteeNames.join("; ") : "None",
      action:
        person.pgaId && navigate ? () => navigate(`/people/${person.pgaId}/membership/committees` as Path) : undefined
    }
  ];
}

export function qualifyingLevelData(person: Person) {
  const rightProgram = [ProgramTypeName.Affiliate, ProgramTypeName.Student, ProgramTypeName.Prospect].includes(
    person.latestProgram?.programType.name!
  );
  const { qualifyingLevel, membershipInterestForm } = person.prospectProgram || {};
  const showData = rightProgram && (!!membershipInterestForm || !!qualifyingLevel);
  if (!showData) return null;

  return {
    status: qualifyingLevel
      ? qualifyingLevel?.endDate
        ? "Complete"
        : formatStatus(qualifyingLevel.status)
      : "Not Started",
    expirationDate: qualifyingLevel?.expirationDate
  };
}

export function backgroundCheckData(person: Person) {
  const rightProgram = [ProgramTypeName.Prospect, ProgramTypeName.Student].includes(
    person.latestProgram?.programType.name!
  );
  const { backgroundCheck } = person.prospectProgram || {};
  const showData = rightProgram && backgroundCheck;
  if (!showData) return null;

  return {
    status: backgroundCheck?.status ? formatStatus(backgroundCheck.status) : "Not Started"
  };
}

export function playingAbilityTestData(person: Person) {
  const rightProgram = [ProgramTypeName.Prospect, ProgramTypeName.Affiliate].includes(
    person.latestProgram?.programType.name!
  );

  const { membershipInterestForm, playingAbilityTestStatus } = person.prospectProgram || {};
  const pat =
    (playingAbilityTestStatus?.submissions || []).length > 0 ? playingAbilityTestStatus?.submissions[0] : null;
  const showData = rightProgram && (!!membershipInterestForm || !!pat);
  if (!showData) return null;

  return {
    status: pat ? formatStatus(pat.result) : "Not Started",
    date: pat?.date
  };
}

export function qualifyingLevelOptions(person: Person): ListItemProps[] {
  const qualifyingLevel = qualifyingLevelData(person);
  const pat = playingAbilityTestData(person);

  const options: ListItemProps[] = [];

  if (qualifyingLevel) {
    const supportingText = qualifyingLevel?.expirationDate
      ? `Expires ${toUTCFormat(qualifyingLevel.expirationDate)}`
      : undefined;

    options.push({
      iconName: "book",
      overline: "Qualifying Level Course",
      headline: qualifyingLevel.status,
      supportingText
    });
  }

  if (pat) {
    options.push({
      iconName: "golf_course",
      overline: "Playing Ability Test (PAT)",
      headline: pat.status,
      supportingText: pat.date && `${toUTCFormat(pat.date)}`
    });
  }

  return options;
}

export function membershipOptions(person: Person, navigate?: (to: Path) => void): ListItemProps[] {
  const program = person.latestProgram;
  if (!program) return [];

  const { band } = program;

  const supportingText = band
    ? `${toFormat(band.startedOn)} - ${
        program.status === ProgramStatusType.ACTIVE ? "Current" : toFormat(band.expiresOn)
      }`
    : undefined;

  const membershipAction =
    person.pgaId && navigate ? () => navigate(`/people/${person.pgaId}/membership/overview` as Path) : undefined;

  return [
    {
      iconName: "badge",
      overline: band?.createdReason ? `Start Reason: ${formatStatus(band.createdReason)}` : undefined,
      headline: band?.classification?.name,
      supportingText,
      action: membershipAction
    }
  ];
}

export function educationLearningOptions(person: Person, navigate?: (to: Path) => void): ListItemProps[] {
  if (!isMemberOrAssociateProgram(person)) return [];

  const { educationLatestCertification: certification } = person;
  const expired = certification?.subscription?.expired;
  const hasActiveCertification = certification && !expired;

  const learningAction =
    person.pgaId && navigate ? () => navigate(`/people/${person.pgaId}/education/learning` as Path) : undefined;

  if (!hasActiveCertification) {
    return [
      {
        iconName: "book",
        overline: "Active Learning",
        headline: "None",
        action: learningAction
      }
    ];
  }

  return [
    {
      iconName: "book",
      overline: certification.careerPath,
      headline: certification.programDescription,
      supportingText: `${toFormat(certification.startDate)} - ${
        certification.endDate ? toFormat(certification.endDate) : "Current"
      }`,
      action: learningAction
    }
  ];
}

export function careerOptions(
  person: Person,
  pgaId?: string,
  navigate?: (to: Path, options?: any) => void,
  canViewCareerPreferences?: boolean,
  canViewCompensationProfiles?: boolean
): ListItemProps[] {
  const options: ListItemProps[] = [...employmentOptions(person, navigate)];

  const hasJobSeekerProfile = person.careerInfo?.hasJobPreferences;
  const jobSeekerProfileModifiedDate = person.careerInfo?.jobPreferencesLastModified
    ? toFormat(person.careerInfo.jobPreferencesLastModified, "MMM d, yyyy")
    : undefined;

  options.push({
    iconName: "business_center",
    overline: "Job Preferences",
    headline: hasJobSeekerProfile ? "Yes" : "No",
    supportingText: jobSeekerProfileModifiedDate ? `Modified ${jobSeekerProfileModifiedDate}` : undefined,
    action:
      canViewCareerPreferences && hasJobSeekerProfile && navigate && pgaId
        ? () => navigate(`/people/${pgaId}/career/job-preferences` as Path)
        : undefined
  });

  const hasCompensationProfile = person.careerInfo?.hasCompensationProfile;
  const compensationProfileModifiedDate = person.careerInfo?.contactLastModifiedCompensationProfile
    ? toFormat(person.careerInfo.contactLastModifiedCompensationProfile, "MMM d, yyyy")
    : undefined;

  options.push({
    iconName: "paid",
    overline: "Compensation Profile",
    headline: hasCompensationProfile ? "Yes" : "No",
    supportingText: compensationProfileModifiedDate ? `Modified ${compensationProfileModifiedDate}` : undefined,
    action:
      canViewCompensationProfiles && hasCompensationProfile && navigate && pgaId
        ? () => navigate(`/people/${pgaId}/career/compensation` as Path)
        : undefined
  });

  const facilityHasGolfDirector = person.careerInfo?.currentEmployerContainsGolfDirector;

  options.push({
    iconName: "location_on",
    overline: "Facility Has Director of Golf",
    headline: hasCompensationProfile ? (facilityHasGolfDirector ? "Yes" : "No") : undefined
  });

  return options;
}

function mostRecentCurrentEmployment(employments: Employment[]): Employment | undefined {
  return [...employments]
    .filter((employment) => !hasEmploymentEnded(employment))
    .sort((a, b) => {
      const aDate = a.startedOn ? new Date(a.startedOn).getTime() : 0;
      const bDate = b.startedOn ? new Date(b.startedOn).getTime() : 0;
      return bDate - aDate;
    })[0];
}

function buildEmploymentOption(employment: Employment, action?: ListItemProps["action"]): ListItemProps {
  const displayTitle = employment.formattedTitle || employment.title;
  const jobTitleWithClassification = employment.jobClassification
    ? `${displayTitle} (${employment.jobClassification})`
    : displayTitle;
  const formattedStartDate = employment.startedOn ? toFormat(employment.startedOn) : null;

  return {
    iconName: "business_center",
    overline: jobTitleWithClassification,
    headline: employment.facility.name,
    copyable: !!employment.facility.name,
    copyText: employment.facility.name ?? undefined,
    supportingText: employment.facility.phone?.phoneFormatted,
    ...(formattedStartDate && {
      tooltipTitle: "Since",
      tooltipSupportingText: formattedStartDate
    }),
    action
  };
}

export function employmentOptions(person: Person, navigate?: (to: Path) => void): ListItemProps[] {
  const employments = person.employments ?? [];
  const employmentTableAction =
    person.pgaId && navigate ? () => navigate(`/people/${person.pgaId}/career/employment` as Path) : undefined;

  const primaryEmployment = employments.find((e) => e.isPrimary);

  if (!primaryEmployment) {
    // Only show unemployed status for active/suspended Members/Associates
    if (isNotTerminatedMemberOrAssociate(person)) {
      if (shouldSuppressUnemployedForClassification(person)) {
        return [];
      }
      // Try to find the most recently ended employment to show previous job info
      const mostRecentEndedEmployment = [...employments]
        .filter((e) => hasEmploymentEnded(e))
        .sort((a, b) => {
          if (!a.endedOn || !b.endedOn) return 0;
          return new Date(b.endedOn).getTime() - new Date(a.endedOn).getTime();
        })[0];

      if (mostRecentEndedEmployment) {
        const displayTitle = mostRecentEndedEmployment.formattedTitle || mostRecentEndedEmployment.title;
        const overlineText =
          displayTitle && mostRecentEndedEmployment.jobClassification
            ? `${displayTitle} (${mostRecentEndedEmployment.jobClassification})`
            : displayTitle;

        return [
          {
            iconName: "business_center",
            headline: "Unemployed",
            overline: overlineText,
            supportingText: mostRecentEndedEmployment.endedOn
              ? `Employment Ended: ${toFormat(mostRecentEndedEmployment.endedOn)}`
              : undefined,
            action: employmentTableAction
          }
        ];
      }

      // No previous employment info available - still show unemployed
      return [
        {
          iconName: "business_center",
          headline: "Unemployed",
          action: employmentTableAction
        }
      ];
    }

    const currentEmployment = mostRecentCurrentEmployment(employments);
    if (currentEmployment) {
      return [buildEmploymentOption(currentEmployment, employmentTableAction)];
    }

    return [];
  }

  return [buildEmploymentOption(primaryEmployment, employmentTableAction)];
}

export const formatStatus = (status?: string, defaultStatus?: string) =>
  status
    ? status
        .split("_")
        .map((word) => capitalize(word.toLowerCase()))
        .join(" ")
    : defaultStatus;

export const getActiveSuspension = (suspensions?: CcmsSuspension[]): CcmsSuspension | undefined =>
  suspensions?.find((s) => !s.endDate || new Date(s.endDate) >= new Date());

/** PAT rows are playing-ability holds, not membership suspensions (CCMS `suspend_cd <> 'PAT'`). */
const PAT_SUSPENSION_CODE = "PAT";

export const getOpenNonPatCcmsSuspension = (suspensions?: CcmsSuspension[] | null): CcmsSuspension | undefined =>
  (suspensions ?? []).find((suspension) => suspension.code !== PAT_SUSPENSION_CODE && !suspension.endDate);

export const hasOpenNonPatCcmsSuspension = (suspensions?: CcmsSuspension[] | null): boolean =>
  Boolean(getOpenNonPatCcmsSuspension(suspensions));

/** When status is Suspended, prefer the open non-PAT row so PAT holds do not label the overlay. */
export const getDisplayedCcmsSuspension = (
  displayedStatus: string | ProgramStatusType | undefined,
  suspensions?: CcmsSuspension[] | null
): CcmsSuspension | undefined => {
  if (displayedStatus === ProgramStatusType.SUSPENDED || displayedStatus === "Suspended") {
    return getOpenNonPatCcmsSuspension(suspensions) ?? getActiveSuspension(suspensions ?? undefined);
  }
  return getActiveSuspension(suspensions ?? undefined);
};

export const overlayActiveProgramStatusWithOpenCcmsSuspension = (
  status: ProgramStatusType | undefined,
  suspensions?: CcmsSuspension[] | null
): ProgramStatusType | undefined => {
  if (status === ProgramStatusType.ACTIVE && hasOpenNonPatCcmsSuspension(suspensions)) {
    return ProgramStatusType.SUSPENDED;
  }
  return status;
};

export const getSuspensionReasonLabel = (suspension: CcmsSuspension): string | undefined =>
  suspension.reasonDescription && suspension.code
    ? `${suspension.reasonDescription} (${suspension.code})`
    : suspension.reasonDescription || suspension.code || undefined;

export function buildProgramStatusCard(
  status: ProgramStatusType | undefined,
  suspensions: CcmsSuspension[] | undefined,
  terminatedReason: string | undefined | null,
  terminatesOn: string | undefined | null,
  defaultLabel: string
): {
  label: string;
  value: string | undefined;
  variant: MetricCardVariant;
  tooltip?: { supportingText: string };
} {
  const displayedStatus = overlayActiveProgramStatusWithOpenCcmsSuspension(status, suspensions);
  const isDeceased = terminatedReason?.toLowerCase() === "deceased";
  const value =
    displayedStatus === ProgramStatusType.TERMINATED && isDeceased ? "Deceased" : formatStatus(displayedStatus);

  let variant: MetricCardVariant;
  if (displayedStatus === ProgramStatusType.ACTIVE) {
    variant = "success";
  } else if (displayedStatus === ProgramStatusType.SUSPENDED) {
    variant = "warn";
  } else if (displayedStatus === ProgramStatusType.TERMINATED) {
    variant = isDeceased ? "info" : "error";
  }

  let label = defaultLabel;
  let tooltip: { supportingText: string } | undefined;

  if (displayedStatus === ProgramStatusType.SUSPENDED) {
    const activeSuspension = getDisplayedCcmsSuspension(displayedStatus, suspensions);
    if (activeSuspension) {
      label = getSuspensionReasonLabel(activeSuspension) ?? label;
      if (activeSuspension.startDate) {
        tooltip = { supportingText: `Suspension Start Date: ${toUTCFormat(activeSuspension.startDate)}` };
      }
    }
  } else if (displayedStatus === ProgramStatusType.TERMINATED) {
    if (terminatedReason) {
      label = formatStatus(terminatedReason) ?? label;
    }
    if (terminatesOn) {
      tooltip = { supportingText: `Termination Date: ${toUTCFormat(terminatesOn)}` };
    }
  }

  return { label, value, variant, ...(tooltip && { tooltip }) };
}

export const toInput = (payload: Record<string, any>) => {
  const result = { ...payload };
  for (const key in result) {
    if (result[key] === "") {
      result[key] = null;
    }
    if (result[key] === undefined) {
      delete result[key];
    }
  }
  return result;
};

export function hasMeaningfulPATStatus(
  pat: PlayingAbilityTestStatus | null | undefined
): pat is PlayingAbilityTestStatus {
  if (!pat) return false;
  return !!(pat.status || pat.activeSuspension || pat.completed || pat.expired || pat.submissions?.length);
}

export function buildPATStatusForNonMembers(playingAbilityTestStatus: PlayingAbilityTestStatus): {
  value: string;
  variant?: "success" | "warn" | "error" | "info";
  tooltip?: { supportingText: string };
} {
  const { status, expired, startDate } = playingAbilityTestStatus || {};
  const normalizedStatus = status?.toLowerCase();
  const formattedStartDate = startDate ? toUTCFormat(startDate) : null;

  // 1. Check for active suspension
  if (playingAbilityTestStatus.activeSuspension) {
    const suspensionDetails = getPATSuspensionDetails(playingAbilityTestStatus);
    const { startDate: suspStartDate, endDate } = playingAbilityTestStatus.activeSuspension;

    let tooltipText = "PAT suspension active";
    if (suspensionDetails) {
      tooltipText = suspensionDetails;
    } else if (suspStartDate) {
      const endDateStr = endDate ? toUTCFormat(endDate) : "Indefinite";
      tooltipText = `PAT Suspension: ${toUTCFormat(suspStartDate)} - ${endDateStr}`;
    }

    return {
      value: getConsolidatedPATStatusLabel("suspended"),
      variant: "error",
      tooltip: { supportingText: tooltipText }
    };
  }

  // 2. Passed (and not expired)
  if (normalizedStatus === "passed" && !expired) {
    return {
      value: getConsolidatedPATStatusLabel("passed"),
      variant: "success",
      tooltip: {
        supportingText: formattedStartDate ? `PAT Passed: ${formattedStartDate}` : "PAT successfully passed"
      }
    };
  }

  // 3. Expired
  if (expired) {
    let tooltipText = "PAT has expired";
    if (playingAbilityTestStatus.expirationDate) {
      tooltipText = `PAT Expired: ${toUTCFormat(playingAbilityTestStatus.expirationDate)}`;
    }
    return {
      value: getConsolidatedPATStatusLabel("expired"),
      variant: "error",
      tooltip: { supportingText: tooltipText }
    };
  }

  // 4. Qualifying
  if (normalizedStatus === "qualified") {
    return {
      value: getConsolidatedPATStatusLabel("qualifying"),
      variant: "info",
      tooltip: {
        supportingText: formattedStartDate ? `PAT Qualifying Score: ${formattedStartDate}` : "PAT has qualifying score"
      }
    };
  }

  // 5. Failed
  if (normalizedStatus === "failed") {
    return {
      value: getConsolidatedPATStatusLabel("failed"),
      variant: "error",
      tooltip: {
        supportingText: formattedStartDate ? `PAT Failed: ${formattedStartDate}` : "PAT attempt failed"
      }
    };
  }

  // 6. Any other known status = In Progress
  if (status !== null && status !== undefined) {
    return { value: getConsolidatedPATStatusLabel("in_progress"), variant: undefined };
  }

  // 7. No status at all = Not Started
  return { value: getConsolidatedPATStatusLabel("not_started"), variant: undefined };
}
